import {
  exerciseAttribute,
  freeAttribute,
  handedOverAttribute,
  missedAttribute,
  queueAttribute,
  recordAttribute,
  versionAttribute,
} from "../content/lianxi/exercise-fields.js";
import {
  browserAudio,
  holdToSpeak,
  type SpeechAudio,
} from "./hold-to-speak.js";
import { itemGate } from "./lianxi-gate.js";
import { blockHandoverCount } from "./lianxi-handover.js";
import { loadPlace, wantsPlace } from "./lianxi-place.js";
import { ExercisePress } from "./lianxi-post.js";
import {
  type ShuociControls,
  type ShuociKeeper,
  type ShuociPlace,
  ShuociExercise,
} from "./lianxi-shuoci.js";
import { type HearVerdict, verdictSounds } from "./lianxi-verdict-sounds.js";
import { said } from "./xiezi-parts.js";

/**
 * Starting a 语音答题 Exercise on a `/lianxi/shuoci/` page.
 *
 * `partials/lianxi/shuoci.html` draws every word, and nothing is fetched but
 * a signed-in learner's place. `./lianxi-place.ts` asks the handler for the
 * place first and writes it onto the block, with `data-cb-lianxi-record`
 * saying where to post, and the Exercise starts at the word the handler left
 * it at. A block with no place on it is a visitor's, who types and keeps
 * nothing. The block is marked ready once the Exercise is built, which is
 * what the browser case waits for.
 *
 * **The button is bound only for a learner with a place.** A recording goes
 * to the handler or nowhere, so a visitor is never asked for the microphone.
 * Enter goes on from a marked word, and submits the field while typing.
 */

/** The block, carrying every word. */
const exerciseSelector = "[data-cb-lianxi-shuoci]";

/** A list of whole numbers off an attribute, as `3,0,5`. */
function indexes(value: string): readonly number[] {
  return value === ""
    ? []
    : value
        .split(",")
        .map((each) => Number(each))
        .filter((at) => Number.isInteger(at) && at >= 0);
}

/**
 * The kept place and the keeper for a block that names where to post, or
 * nothing for a block that keeps no place.
 */
function keptSpeaking(
  block: HTMLElement,
  fetching: typeof fetch,
): { readonly kept: ShuociPlace; readonly keeper: ShuociKeeper } | undefined {
  const address = said(block, recordAttribute);
  const slug = said(block, exerciseAttribute);

  if (address === "" || slug === "") {
    return undefined;
  }

  return {
    kept: {
      queue: indexes(said(block, queueAttribute)),
      missed: indexes(said(block, missedAttribute)),
    },
    keeper: {
      press: new ExercisePress({ fetch: fetching, address, slug }),
      version: block.dataset[versionAttribute] ?? "",
      handedOver: block.dataset[handedOverAttribute] !== undefined,
      handoverCount: blockHandoverCount(block),
      free: block.dataset[freeAttribute] !== undefined,
    },
  };
}

/** A part of the block, by the name after `data-cb-shuoci-`. */
function partOf(block: HTMLElement, name: string): HTMLElement | null {
  return block.querySelector<HTMLElement>(`[data-cb-shuoci-${name}]`);
}

/** The controls under the words, or nothing for a block short of one. */
function controlsOf(block: HTMLElement): ShuociControls | undefined {
  const note = partOf(block, "note");
  const typing = block.querySelector<HTMLDetailsElement>(
    "details[data-cb-shuoci-typing]",
  );
  const field = block.querySelector<HTMLInputElement>(
    "input[data-cb-shuoci-typed]",
  );

  return note === null || typing === null || field === null
    ? undefined
    : {
        note,
        speak: block.querySelector<HTMLButtonElement>("[data-cb-shuoci-speak]"),
        status: partOf(block, "status"),
        visitor: partOf(block, "visitor"),
        typing,
        field,
      };
}

/** Build the Exercise inside one block. */
function openSpeaking(
  block: HTMLElement,
  fetching: typeof fetch,
  hear: HearVerdict | undefined,
  audio: SpeechAudio,
): void {
  const items = [
    ...block.querySelectorAll<HTMLElement>("[data-cb-shuoci-word]"),
  ];
  const controls = controlsOf(block);
  const progress = partOf(block, "progress");
  const next = block.querySelector<HTMLButtonElement>("[data-cb-shuoci-next]");
  const mark = partOf(block, "mark");
  const finished = partOf(block, "finished");
  const finishedLine = partOf(block, "finished-line");
  const form = block.querySelector<HTMLFormElement>("[data-cb-shuoci-form]");

  if (
    items.length === 0 ||
    controls === undefined ||
    progress === null ||
    next === null ||
    mark === null ||
    finished === null ||
    finishedLine === null ||
    form === null
  ) {
    return;
  }

  const kept = keptSpeaking(block, fetching);
  const exercise = new ShuociExercise({
    items,
    controls,
    progress,
    meter: partOf(block, "meter"),
    next,
    mark,
    finished,
    finishedLine,
    kept: kept?.kept,
    keeper: kept?.keeper,
    gate: itemGate(block),
    hear,
    audio,
  });

  if (exercise.canSpeak() && controls.speak !== null) {
    holdToSpeak({
      button: controls.speak,
      status: controls.status,
      recordingOn: controls.speak.parentElement ?? block,
      audio,
      onSpoken: (spoken) => {
        void exercise.speak(spoken);
      },
      onMicrophoneRefused: () => {
        controls.typing.open = true;
        controls.field.focus();
      },
    });
  }

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    void exercise.type();
  });
  next.addEventListener("click", () => {
    exercise.next();
  });
  partOf(block, "again")?.addEventListener("click", () => {
    exercise.again();
  });

  // The keys are heard on the whole page, so a block taken off it stops
  // hearing them at the next key, rather than answering for a page gone.
  const onKey = (event: KeyboardEvent): void => {
    if (!block.isConnected) {
      block.ownerDocument.removeEventListener("keydown", onKey);

      return;
    }

    if (
      event.key === "Enter" &&
      exercise.isMarked() &&
      !(
        event.target instanceof Element &&
        event.target.closest("button, a[href], summary, input") !== null &&
        event.target !== next
      )
    ) {
      event.preventDefault();
      exercise.next();
    }
  };

  block.ownerDocument.addEventListener("keydown", onKey);
  exercise.start();
  block.dataset["cbLianxiReady"] = "";
}

/**
 * Start the 语音答题 Exercise on every block under `root`.
 * @param root - The page, or the part of it to look in.
 * @param fetching - How to post. The window's own unless a test says otherwise.
 * @param soundsFor - What plays a verdict's note. 复习's notes unless a test
 *   says otherwise.
 * @param audio - What records and plays. The browser's unless a test says
 *   otherwise.
 */
export function startShuoci(
  root: ParentNode,
  fetching: typeof fetch = (input, init): Promise<Response> =>
    fetch(input, init),
  soundsFor: (page: Document) => HearVerdict | undefined = verdictSounds,
  audio: SpeechAudio = browserAudio,
): void {
  for (const block of root.querySelectorAll<HTMLElement>(exerciseSelector)) {
    const hear = soundsFor(block.ownerDocument);

    if (wantsPlace(block)) {
      void loadPlace(block, fetching).then(() => {
        openSpeaking(block, fetching, hear, audio);
      });
    } else {
      openSpeaking(block, fetching, hear, audio);
    }
  }
}
